import React, {ChangeEvent, useEffect, useRef} from "react";
import {DateTime} from "luxon";
import {useField} from "formik";
import {systemZone} from "../../../../services/date";
import {concatenate} from "../../../../services/string";
import FieldHelp from "./FieldHelp";

// Types ----

type InputType = 'datetime-local' | 'date';

type CommonProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'defaultValue' | 'onChange'
> & {
  name: string;
};

type DateProps = CommonProps & {
  type: 'date';
  zone: string; // Required to prevent off-by-one issues
};

type DateTimeProps = CommonProps & {
  type: 'datetime-local';
  zone?: string; // Optional, defaults to system zone
};

type DateFieldProps = DateProps | DateTimeProps;

// Helpers ----

function localTime(value: string | number, zone: string, type: InputType): string {
  // Get the date time in the given zone (if value has no offset it'll be interpreted in the given zone, otherwise it's converted to that zone)
  const dateTime = DateTime.fromISO(value ? value.toString() : '', {zone});

  // If valid date format it for the input
  if (!dateTime.isValid) return '';

  // Return the appropriate format
  return type === 'date' ? dateTime.toISODate() : dateTime.toFormat("yyyy-LL-dd'T'HH:mm");
}

function universalTime(value: string, zone: string): string {
  // Interpret the naive string (no offset) in the given zone
  const dateTime = DateTime.fromISO(value, { zone });

  // Convert to UTC and return as ISO string, or empty string if invalid
  return dateTime.isValid ? dateTime.toUTC().toISO() : '';
}

function adjustedUniversalTime(value: string, zone: string, previousZone: string): string {
  // Get the date time in the previous zone so we know what the wall clock time was
  const prevDateTime = DateTime.fromISO(value).setZone(previousZone);

  // Get the same wall clock time as the previous zone but in the new zone
  const dateTime = prevDateTime.setZone(zone, { keepLocalTime: true });

  // Convert to UTC and return as ISO string, or empty string if invalid
  return dateTime.isValid ? dateTime.toUTC().toISO() : '';
}

// Component ----

export default function DateField({ name, type = 'date', zone = systemZone(), ...rest }: DateFieldProps) {
  const [field, meta, helpers] = useField(name);

  // Keep track of the previous timezone
  const previousTimezoneRef = useRef(zone);

  useEffect(() => {
    // If the timezone has changed, update the field value to reflect the new timezone
    if (zone && zone !== previousTimezoneRef.current && type === 'datetime-local') {
      helpers.setValue(adjustedUniversalTime(field.value, zone, previousTimezoneRef.current)).catch(console.error);
      previousTimezoneRef.current = zone;
    }
  }, [zone, field.value, helpers, type]);

  // Handle the change event from the input
  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;

    switch(type) {
      case "date":
        void helpers.setValue(localTime(value, zone, type));
        break;
      case "datetime-local":
        void helpers.setValue(universalTime(value, zone));
        break;
    }
  }

  return (
    <>
      <input
        {...rest}
        id={rest.id ?? field.name}
        name={field.name}
        type={type}
        value={localTime(field.value, zone, type)}
        onChange={handleChange}
        min={rest.min ? localTime(rest.min, zone, type) : undefined}
        max={rest.max ? localTime(rest.max, zone, type) : undefined}
        className={concatenate(
          (!!meta.error && meta.touched) && 'border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500',
          'focus:ring-brand focus:border-brand border-gray-300 block w-full sm:text-sm rounded-md disabled:opacity-40'
        )}
      />

      {(!!meta.error && meta.touched) && <FieldHelp isError>{meta.error}</FieldHelp>}
    </>
  );
}